/* eslint-disable @typescript-eslint/naming-convention */

import { DatabaseGetSchemaResult } from '../../../types/database';
import * as commonDefinitions from './commonDefinitions';

function getSchema(): { tracked_books: DatabaseGetSchemaResult<'tracked_books'>; } {
  return {
    tracked_books: {
      schema: {
        primaryKey: 'id',
        additionalProperties: false,
        properties: {
          id: commonDefinitions.getString(100),
          created: commonDefinitions.getIndexableTimestamp(),
          updated: commonDefinitions.getIndexableTimestamp(),
          title: commonDefinitions.orType(commonDefinitions.getString(3500), 'null'),
          author: commonDefinitions.orType(commonDefinitions.getString(2500), 'null'),
          cover_url: commonDefinitions.orType(commonDefinitions.getString(20000), 'null'),
          isbn: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          asin: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          is_sample: commonDefinitions.orType(commonDefinitions.booleanInteger, 'null'),
          progress: commonDefinitions.orType({
            ...commonDefinitions.anyNumber,
            minimum: 0,
            maximum: 1,
          }, 'null'),
          creation_origin: {
            type: 'string',
            enum: [
              'bookstore_ebook',
              'uploaded_ebook',
              'physical',
              'kindle',
              'goodreads',
              'bookwise',
            ],
          },
          goodreads_book_id: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          kindle_user_book_id: commonDefinitions.orType(commonDefinitions.integer, 'null'),
          status: {
            type: 'string',
            enum: [
              'unsorted',
              'want_to_read',
              'currently_reading',
              'finished',
              'deleted',
              'did_not_finish',
            ],
          },
          description: commonDefinitions.orType(commonDefinitions.getString(50000), 'null'),
          description_text: commonDefinitions.orType(commonDefinitions.getString(50000), 'null'),
          short_description: commonDefinitions.orType(commonDefinitions.getString(1000), 'null'),
          publisher_description_is_low_quality: commonDefinitions.booleanInteger,
          genre_classification: commonDefinitions.orType(
            {
              type: 'array',
              items: commonDefinitions.getString(128),
            } as Parameters<typeof commonDefinitions.orType>[0],
            'null',
          ),
          published_at: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),

          catalog_product_id: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          document_id: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          bookwise_profile_id: commonDefinitions.orType(commonDefinitions.getIndexableInteger(), 'null'),
          last_read_at: commonDefinitions.orType(commonDefinitions.getIndexableTimestamp(), 'null'),
          started_reading_at: commonDefinitions.orType(commonDefinitions.getIndexableTimestamp(), 'null'),
          finished_reading_at: commonDefinitions.orType(commonDefinitions.getIndexableTimestamp(), 'null'),
          status_changed_at: commonDefinitions.orType(commonDefinitions.getIndexableTimestamp(), 'null'),
          reading_time: commonDefinitions.orType(commonDefinitions.getString(100), 'null'),
          page_count: commonDefinitions.orType(commonDefinitions.integer, 'null'),
          word_count: commonDefinitions.orType(commonDefinitions.integer, 'null'),
          features: {
            type: 'object',
            // so that we can gracefully add new feature flags in the future without breaking the schema on clients.
            additionalProperties: true,
            properties: {
              recap_summaries: { type: 'boolean' },
              ghostreader: { type: 'boolean' },
              word_lookup: { type: 'boolean' },
            },
            required: ['recap_summaries', 'ghostreader', 'word_lookup'],
          },
          session_count: commonDefinitions.integer,
        },
        required: [
          'id',
          'created',
          'updated',
          'status',
          'features',
          'session_count',
        ],
        type: 'object',
        version: 8,
      },
      migrationStrategies: {
        1: (oldDoc) => ({
          ...oldDoc,
          features: {
            // default to permissive so we don't break existing features
            recap_summaries: true,
            ghostreader: true,
            word_lookup: true,
          },
        }),
        2: (oldDoc) => ({
          ...oldDoc,
          started_reading_at: null,
          finished_reading_at: null,
        }),
        3: (oldDoc) => ({
          ...oldDoc,
          short_description: null,
        }),
        4: (oldDoc) => ({
          ...oldDoc,
          session_count: 0,
        }),
        5: (oldDoc) => ({
          ...oldDoc,
          genre_classification: null,
        }),
        6: (oldDoc) => ({
          ...oldDoc,
          publisher_description_is_low_quality: 0,
        }),
        7: (oldDoc) => ({
          ...oldDoc,
          status_changed_at: null,
        }),
        8: (oldDoc) => ({
          ...oldDoc,
          goodreads_book_id: null,
          kindle_user_book_id: null,
        }),
      },
    },
  } as const;
}

export default {
  getSchema,
};
